import { useEffect, useRef, useState } from 'react';

import { ScrollArea } from '@radix-ui/themes';
import cn from 'classnames';

const THRESHOLD_STEPS = 101;
const THRESHOLDS = Array.from(
  { length: THRESHOLD_STEPS },
  (_, i) => i / (THRESHOLD_STEPS - 1)
);

export function FlyScrollShadowContainer({
  children,
  className,
  gradientColor = 'var(--gray-3)',
  gradientHeight = 32,
  hideTop,
  hideBottom,
  contentClassName,
  scrollRef: externalScrollRef,
  onScroll,
  scrollViewportTestId,
}: {
  children: React.ReactNode;
  className?: string;
  gradientColor?: string;
  gradientHeight?: number;
  hideTop?: boolean;
  hideBottom?: boolean;
  contentClassName?: string;
  /** Optional external ref to the scroll container (e.g. for virtualizers). */
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  /** Optional scroll event handler on the scroll container. */
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  /** `data-testid` on Radix ScrollArea (viewport); pair with TanstackTable virtual scroll automation. */
  scrollViewportTestId?: string;
}) {
  const internalScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef ?? internalScrollRef;
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const [topHeight, setTopHeight] = useState(0);
  const [bottomHeight, setBottomHeight] = useState(0);

  useEffect(() => {
    const root = scrollRef.current;
    const topEl = topSentinelRef.current;
    const bottomEl = bottomSentinelRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const height = Math.round(
            gradientHeight * (1 - entry.intersectionRatio)
          );
          if (entry.target === topEl) {
            setTopHeight(height);
          } else {
            setBottomHeight(height);
          }
        }
      },
      { root, threshold: THRESHOLDS }
    );

    if (topEl) observer.observe(topEl);
    if (bottomEl) observer.observe(bottomEl);

    return () => observer.disconnect();
  }, [gradientHeight, scrollRef]);

  const sentinelStyle = {
    height: gradientHeight,
    width: '100%',
    flexShrink: 0,
    pointerEvents: 'none' as const,
  };

  return (
    <div className={cn('relative', className)}>
      {!hideTop && topHeight > 0 && (
        <div
          className='inset-x-0 top-0 pointer-events-none absolute z-10'
          style={{
            height: topHeight,
            background: `linear-gradient(to top, transparent, ${gradientColor})`,
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          }}
        />
      )}
      <ScrollArea
        ref={scrollRef}
        data-testid={scrollViewportTestId}
        scrollbars='vertical'
        type='auto'
        className='pr-4'
        onScroll={onScroll}
      >
        <div className={cn('h-full', contentClassName)}>
          <div
            ref={topSentinelRef}
            style={{ ...sentinelStyle, marginBottom: -gradientHeight }}
          />
          {children}
          <div
            ref={bottomSentinelRef}
            style={{ ...sentinelStyle, marginTop: -gradientHeight }}
          />
        </div>
      </ScrollArea>
      {!hideBottom && bottomHeight > 0 && (
        <div
          className='inset-x-0 bottom-0 pointer-events-none absolute z-10'
          style={{
            height: bottomHeight,
            background: `linear-gradient(to bottom, transparent, ${gradientColor})`,
            borderBottomLeftRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
          }}
        />
      )}
    </div>
  );
}
